import React, { useCallback, useEffect, useState } from 'react';
import tokenService from '../utils/tokenService';
import termAcceptanceApi from '../services/termAcceptanceApi';
import MerchantAgreement from '../views/merchant-agreement/merchant-agreement';
import WalkingSpinner from './spinner/WalkingSpinner';

const MerchantAcceptanceGuard = ({ children }) => {
  const [status, setStatus] = useState(null);
  const [merchantTerms, setMerchantTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAccepted = useCallback(async () => {
    if (!tokenService.getLocalAccessToken()) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await termAcceptanceApi.isAccepted({ termName: 'merchant' });
      const data = res.data?.data;
      const accepted = data?.isAccepted === true;
      setStatus(accepted);
      setMerchantTerms(accepted ? [] : (data?.merchantTerms ?? []));
    } catch (err) {
      setError(err?.response?.data ?? 'Failed to verify term acceptance.');
      setStatus(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAccepted();
  }, [checkAccepted]);

  const handleAccepted = useCallback(() => {
    setStatus(true);
    setMerchantTerms([]);
  }, []);

  if (loading) {
    return <WalkingSpinner fullScreen size={88} />;
  }

  const showOverlay = status === false;

  return (
    <>
      {children}
      {showOverlay && (
        <div className="fixed inset-0 top-[8vh] z-[5] flex items-start justify-center overflow-y-auto bg-black/50 backdrop-blur-sm">
          {error ? (
            <div className="mt-[12vh] flex flex-col items-center gap-4 rounded-md bg-white p-8 shadow-lg dark:bg-slate-800">
              <p className="text-center text-gray-700 dark:text-slate-300">
                {typeof error === 'string'
                  ? error
                  : 'Unable to verify agreement status.'}
              </p>
              <button
                type="button"
                onClick={checkAccepted}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 dark:bg-sky-600 dark:hover:bg-sky-700"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="w-full py-12 px-4 sm:px-6">
              <MerchantAgreement
                merchantTerms={merchantTerms}
                onAccepted={handleAccepted}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default MerchantAcceptanceGuard;
